import React, { useEffect, useState } from 'react';
import {
    SimpleForm,
    TextInput,
    NumberInput,
    SelectInput,
    SelectArrayInput,
    required,
    useDataProvider,
    maxLength,
    email,
    regex,
    minValue,
    maxValue,
    Toolbar,
    SaveButton,
    DeleteButton,
    useGetIdentity
} from 'react-admin';
import { Grid, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { UserRole } from '../types/types';

const CustomToolbar = () => {
    const { identity } = useGetIdentity();
    const userRole = identity?.role_id;

    return (
        <Toolbar>
            <SaveButton />
            {userRole === UserRole.Admin && <DeleteButton />}
        </Toolbar>
    );
};

interface DirectoryContactFormProps {
    isEdit: boolean;
}

export const DirectoryContactForm: React.FC<DirectoryContactFormProps> = ({ isEdit }) => {
    const [isLoading, setIsLoading] = useState(true);
    const dataProvider = useDataProvider();
    const [subcategories, setSubcategories] = useState<{ id: number; name: string; displayName: string }[]>([]);
    const [contactSubcategories, setContactSubcategories] = useState<number[]>([]);
    const [states, setStates] = useState<{ id: number; state_name: string }[]>([]);

    const validateGST = regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GST number');
    const validatePin = regex(/^[0-9]{6}$/, 'Invalid PIN');
    const validatePhoneLandline = regex(/^[0-9-]+$/, 'Invalid landline number');
    const validatePhoneMobile = regex(/^[0-9]{10}$/, 'Invalid mobile number');

    const params = useParams();
    const contactId = params.id;

    useEffect(() => {
        setIsLoading(true);
        const fetchData = async () => {
            try {
                // Fetch all directory subcategories with their categories
                const subcategoriesResponse = await dataProvider.getList('directorysubcategories', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'subcategory_name', order: 'ASC' },
                    filter: {}
                });
                
                // Format subcategories with "Category - Subcategory" display
                const formattedSubcategories = subcategoriesResponse.data.map((sub: any) => ({
                    id: sub.id,
                    name: sub.subcategory_name,
                    displayName: `${sub.category_name} - ${sub.subcategory_name}`
                }));
                setSubcategories(formattedSubcategories);

                // Fetch contact's subcategories if editing
                if (isEdit && contactId) {
                    const contactSubcategoriesResponse = await dataProvider.getList(`directorycontacts/getcontactsubcategories/${contactId}`, {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'subcategory_name', order: 'ASC' },
                        filter: {}
                    });
                    setContactSubcategories(contactSubcategoriesResponse.data.map((sub: any) => sub.id));
                }

                // Fetch states for the dropdown
                const statesResponse = await dataProvider.getList('states', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'state_name', order: 'ASC' },
                    filter: {}
                });
                setStates(statesResponse.data);

                setIsLoading(false);
            } catch (error) {
                console.error(error);
                setIsLoading(false);
            }
        };

        fetchData();
    }, [contactId, isEdit, dataProvider]);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <SimpleForm toolbar={<CustomToolbar />}>
            <Grid container spacing={2}>
                {/* Contact Info */}
                <Grid item xs={12}>
                    <Typography variant="h6">Contact Info</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextInput source="company_name" label="Company Name" fullWidth validate={[required(), maxLength(100)]} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextInput source="gst_number" label="GST Number" fullWidth validate={validateGST} />
                </Grid>

                {/* Personal Info */}
                <Grid item xs={6} sm={6}>
                    <TextInput source="first_name" label="First Name" fullWidth validate={[required(), maxLength(50)]} />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <TextInput source="last_name" label="Last Name" fullWidth validate={[maxLength(50)]} />
                </Grid>
                <Grid item xs={12}>
                    <TextInput source="email_address" label="Email Address" fullWidth validate={[email()]} />
                </Grid>

                {/* Address */}
                <Grid item xs={12} sm={6}>
                    <TextInput source="address_line1" label="Address Line 1" fullWidth validate={[maxLength(100)]} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextInput source="address_line2" label="Address Line 2" fullWidth validate={[maxLength(100)]} />
                </Grid>
                <Grid item xs={4}>
                    <TextInput source="address_city" label="City" fullWidth validate={[maxLength(50)]} />
                </Grid>
                <Grid item xs={4}>
                    <SelectInput
                        source="address_state"
                        label="State"
                        choices={states}
                        optionText="state_name"
                        optionValue="id"
                        fullWidth
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextInput source="address_pin" label="PIN" fullWidth validate={validatePin} />
                </Grid>

                {/* Contact */}
                <Grid item xs={6}>
                    <TextInput source="phone_landline" label="Landline Phone" fullWidth validate={validatePhoneLandline} />
                </Grid>
                <Grid item xs={6}>
                    <TextInput source="phone_mobile" label="Mobile Phone" fullWidth validate={validatePhoneMobile} />
                </Grid>

                {/* Display Priority */}
                <Grid item xs={12} sm={6}>
                    <NumberInput
                        source="display_priority"
                        label="Display Priority"
                        fullWidth
                        validate={[minValue(0), maxValue(100)]}
                        helperText="Priority from 0-100. Higher priority contacts appear first. Default: 0"
                        defaultValue={0}
                    />
                </Grid>

                {/* Subcategories */}
                <Grid item xs={12}>
                    <Typography variant="h6">Directory Subcategories</Typography>
                </Grid>
                <Grid item xs={12}>
                    <SelectArrayInput
                        label="Subcategories"
                        source="subcategories"
                        choices={subcategories}
                        defaultValue={contactSubcategories}
                        optionText="displayName"
                        optionValue="id"
                        translateChoice={false}
                        fullWidth
                    />
                </Grid>
            </Grid>
        </SimpleForm>
    );
};
